import { useQuery } from "@tanstack/react-query";
import {
  fetchCustomers,
  fetchMemos,
  fetchProject,
  fetchProjectLinks,
  fetchProjectMemos,
  fetchProjects,
  fetchUsers,
} from "../../api/client";
import {
  USERS_KEY,
  customersKey,
  projectKey,
  projectLinksKey,
  projectsKey,
} from "../../api/queries";

/**
 * メモの本文の「@」補完(SPEC §13.6)。候補は種類のアイコン付きで 1 つの一覧に出し、
 * 選ぶと Markdown として本文に入る。
 */
export type MentionKind = "USER" | "LINK" | "MEMO" | "PROJECT" | "CUSTOMER";

export interface MentionItem {
  /** 一覧の中で一意(種類 + ID)。 */
  key: string;
  kind: MentionKind;
  /** 一覧に出す名前。 */
  label: string;
  /** 補足(メールや URL)。絞り込みにも使う。 */
  detail: string;
  /** 本文に入る文字列。 */
  insert: string;
}

export const MENTION_KIND_LABELS: Record<MentionKind, string> = {
  USER: "人",
  LINK: "外部リンク",
  MEMO: "メモ",
  PROJECT: "プロジェクト",
  CUSTOMER: "顧客",
};

/** 一覧に出す件数の上限。 */
export const MENTION_LIMIT = 8;

/** Markdown のリンクの文字に入れると壊れる記号を逃がす。 */
const linkText = (s: string) => s.replace(/([[\]\\])/g, "\\$1");
/** URL の中の括弧・空白は Markdown のリンクを壊すので、%XX にする(encodeURIComponent は括弧を変えない)。 */
const linkHref = (href: string) =>
  href.replace(/[()\s]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`);
const link = (text: string, href: string) => `[${linkText(text)}](${linkHref(href)})`;

/** メモの画面へのリンク(開くと、その画面でメモのダイアログが開く)。 */
export const memoHref = (memoId: string, projectId: string | null) =>
  projectId === null ? `/?memo=${memoId}` : `/projects/${projectId}?memo=${memoId}`;

/**
 * カーソルの直前が「@ + 語」なら、その語と「@」の位置を返す。「@」の前は行頭か空白に限る
 * (メールアドレスの途中の @ では出さない)。語に空白は含めない。
 */
export function mentionQueryAt(
  text: string,
  cursor: number,
): { query: string; start: number } | null {
  const before = text.slice(0, cursor);
  const m = /(^|[\s(（])@([^\s@]{0,30})$/.exec(before);
  if (!m) return null;
  return { query: m[2] ?? "", start: cursor - (m[2] ?? "").length - 1 };
}

/** 候補を絞り込む(大文字小文字・全角半角を区別しない)。名前の先頭で一致するものを先にする。 */
export function filterMentions(items: readonly MentionItem[], query: string): MentionItem[] {
  const norm = (s: string) => s.normalize("NFKC").toLowerCase();
  const q = norm(query.trim());
  if (q === "") return items.slice(0, MENTION_LIMIT);
  const scored: { item: MentionItem; score: number }[] = [];
  for (const item of items) {
    const label = norm(item.label);
    if (label.startsWith(q)) scored.push({ item, score: 0 });
    else if (label.includes(q)) scored.push({ item, score: 1 });
    else if (norm(item.detail).includes(q)) scored.push({ item, score: 2 });
  }
  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, MENTION_LIMIT)
    .map((s) => s.item);
}

/** 「@語」を選んだ候補で置き換えた本文と、置き換えたあとのカーソルの位置。 */
export function applyMention(
  text: string,
  at: { start: number; end: number },
  item: MentionItem,
): { text: string; cursor: number } {
  const inserted = `${item.insert} `;
  return {
    text: text.slice(0, at.start) + inserted + text.slice(at.end),
    cursor: at.start + inserted.length,
  };
}

/**
 * 補完の候補を集める。プロジェクトのメモなら、そのプロジェクトの参加者・外部リンク・メモ・顧客。
 * ホームのメモなら、社内のユーザー・自分が見られるメモ・プロジェクト・顧客。
 * enabled のときだけ取る(本文を編集できるときだけ)。取れなかった種類は出さない。
 */
export function useMentionItems(projectId: string | null, enabled: boolean): MentionItem[] {
  const inProject = enabled && projectId !== null;
  const atHome = enabled && projectId === null;
  const pid = projectId ?? "";
  const project = useQuery({
    queryKey: projectKey(pid),
    queryFn: () => fetchProject(pid),
    enabled: inProject,
  });
  const links = useQuery({
    queryKey: projectLinksKey(pid),
    queryFn: () => fetchProjectLinks(pid),
    enabled: inProject,
  });
  const projectMemos = useQuery({
    queryKey: ["memos", "list", "project", pid, {}],
    queryFn: () => fetchProjectMemos(pid),
    enabled: inProject,
  });
  const users = useQuery({
    queryKey: USERS_KEY,
    queryFn: fetchUsers,
    enabled: atHome,
    staleTime: 60_000,
  });
  const homeMemos = useQuery({
    queryKey: ["memos", "list", {}],
    queryFn: () => fetchMemos({}),
    enabled: atHome,
  });
  const projects = useQuery({
    queryKey: projectsKey(),
    queryFn: () => fetchProjects(),
    enabled: atHome,
  });
  const customers = useQuery({
    queryKey: customersKey("ACTIVE"),
    queryFn: () => fetchCustomers("ACTIVE"),
    enabled: atHome,
  });

  const items: MentionItem[] = [];
  const person = (key: string, name: string, email: string) =>
    items.push({
      key: `USER:${key}`,
      kind: "USER",
      label: name,
      detail: email,
      insert: `@${name}`,
    });
  const memo = (id: string, title: string, pid: string | null) => {
    const label = title || "無題のメモ";
    items.push({
      key: `MEMO:${id}`,
      kind: "MEMO",
      label,
      detail: "",
      insert: link(label, memoHref(id, pid)),
    });
  };
  if (inProject) {
    const p = project.data?.project;
    for (const m of p?.participants ?? []) {
      if (!m.active) continue;
      person(m.email ?? m.displayName, m.displayName || m.email || "", m.email ?? "");
    }
    for (const l of links.data?.links ?? []) {
      if (!l.url) continue;
      const label = l.displayName || l.url;
      items.push({
        key: `LINK:${l.id}`,
        kind: "LINK",
        label,
        detail: l.url,
        insert: link(label, l.url),
      });
    }
    for (const m of projectMemos.data?.memos ?? []) if (!m.archived) memo(m.id, m.title, pid);
    for (const c of p?.customers ?? [])
      items.push({
        key: `CUSTOMER:${c.customerId}`,
        kind: "CUSTOMER",
        label: c.customerName,
        detail: "",
        insert: c.customerName,
      });
  }
  if (atHome) {
    for (const u of users.data?.users ?? []) {
      if (u.archived) continue;
      person(
        u.email,
        u.nameRegistered ? u.displayName : u.email,
        `${u.email} ${u.familyNameKana}${u.givenNameKana}`,
      );
    }
    for (const m of homeMemos.data?.memos ?? []) memo(m.id, m.title, null);
    for (const p of projects.data?.projects ?? [])
      items.push({
        key: `PROJECT:${p.id}`,
        kind: "PROJECT",
        label: p.name,
        detail: p.code ?? "",
        insert: link(p.name, `/projects/${p.id}`),
      });
    for (const c of customers.data?.customers ?? [])
      items.push({
        key: `CUSTOMER:${c.id}`,
        kind: "CUSTOMER",
        label: c.name,
        detail: "",
        insert: c.name,
      });
  }
  return items;
}
